import { registerMutation, useMutation } from 'academy-common-lib/utils/DataFetchingClient';
import { storeTimeAndReturnData } from '../../utils/ajaxUtils';
import { getTrackExamPageUrl } from '../../utils/RoutingUtils';
import { useFullTrack } from '../tracks';
import { BASE_URI_PROGRESS } from '../../constants/ApiConstants';
import http from 'hub-http/clients/apiClient';
import getLang from 'LearningCenterUI/utils/getLang';
import { useCallback } from 'react';
import { addQuestionSubmissionCache, GET_ALL_PROGRESS, setMostRecentExamAttemptCache, useTrackProgress, useTrackProgressById } from '../userProgress';
import { MS_IN_A_MINUTE, MS_IN_A_DAY, TRACK_AWARD_EXPIRY_DAYS } from '../../constants/TimeConstants';
import { getPlaylistIdFromUrl } from '../../utils/UrlParamUtils';
import maxBy from 'hs-lodash/maxBy';
const EXAM_ATTEMPTS_API_URL = `${BASE_URI_PROGRESS}/exam-attempts`;
const START_EXAM = registerMutation({
  fieldName: 'startExam',
  args: ['examId', 'language'],
  fetcher({
    examId,
    language = getLang()
  }) {
    return http.postWithResponse(`${EXAM_ATTEMPTS_API_URL}/${examId}/start/${language}`).then(storeTimeAndReturnData);
  }
});
const RESUME_EXAM = registerMutation({
  fieldName: 'resumeExam',
  args: ['attemptId'],
  fetcher({
    attemptId
  }) {
    return http.postWithResponse(`${EXAM_ATTEMPTS_API_URL}/${attemptId}/resume`).then(storeTimeAndReturnData);
  }
});
const SUBMIT_EXAM_QUESTION = registerMutation({
  fieldName: 'submitExamQuestion',
  args: ['attemptId', 'questionId', 'answerIds'],
  fetcher({
    attemptId,
    questionId,
    answerIds
  }) {
    return http.post(`${EXAM_ATTEMPTS_API_URL}/${attemptId}/question-submissions`, {
      data: {
        questionId,
        answerIds
      }
    });
  }
});
const VALIDATE_EXAM = registerMutation({
  fieldName: 'validateExam',
  args: ['attemptId', 'trackId', 'playlistId'],
  fetcher({
    attemptId,
    trackId,
    playlistId
  }) {
    return http.postWithResponse(`${EXAM_ATTEMPTS_API_URL}/${attemptId}/validate/${trackId}`, {
      query: {
        playlistId
      }
    }).then(storeTimeAndReturnData);
  }
});
const SEND_EMAIL_BREAKDOWN = registerMutation({
  fieldName: 'sendEmailBreakdown',
  args: ['attemptId', 'trackId', 'language'],
  fetcher({
    attemptId,
    trackId,
    language = getLang()
  }) {
    return http.post(`${BASE_URI_PROGRESS}/exam-breakdowns`, {
      data: {
        language,
        attemptId,
        trackId,
        examPath: getTrackExamPageUrl(trackId.toString()).slice(1)
      }
    });
  }
});
export const useExam = (trackId, language) => {
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(trackId, language);
  return {
    exam: track ? track.exam : null,
    examLoading: trackLoading,
    examError: trackError
  };
};
export const useStartExam = () => {
  const [mutate, {
    loading: startExamLoading,
    data: startExamData,
    error: startExamError,
    called: startExamCalled
  }] = useMutation(START_EXAM);
  const startExam = useCallback((trackId, examId, language = getLang()) => {
    return mutate({
      variables: {
        examId,
        language
      },
      update(__, newData) {
        if (newData.data) {
          setMostRecentExamAttemptCache(trackId, newData.data.startExam);
        }
      }
    }).then(result => result.data && result.data.startExam);
  }, [mutate]);
  return [startExam, {
    startExamData,
    startExamLoading,
    startExamError,
    startExamCalled
  }];
};
export const useResumeExam = () => {
  const [mutate, {
    loading: resumeExamLoading,
    data: resumeExamData,
    error: resumeExamError,
    called: resumeExamCalled
  }] = useMutation(RESUME_EXAM);
  const resumeExam = useCallback((trackId, attemptId) => {
    return mutate({
      variables: {
        attemptId
      },
      update(__, newData) {
        if (newData.data) {
          setMostRecentExamAttemptCache(trackId, newData.data.resumeExam);
        }
      }
    }).then(result => result.data && result.data.resumeExam);
  }, [mutate]);
  return [resumeExam, {
    resumeExamData,
    resumeExamLoading,
    resumeExamError,
    resumeExamCalled
  }];
};
export const useSubmitExamQuestion = () => {
  const [mutate, {
    loading: submitExamQuestionLoading,
    data: submitExamQuestionData,
    error: submitExamQuestionError,
    called: submitExamQuestionCalled
  }] = useMutation(SUBMIT_EXAM_QUESTION);
  const submitExamQuestion = useCallback((trackId, attemptId, questionId, answerIds) => {
    return mutate({
      variables: {
        attemptId,
        questionId,
        answerIds
      },
      update() {
        addQuestionSubmissionCache(trackId, attemptId, questionId, answerIds);
      }
    }).then(result => result.data);
  }, [mutate]);
  return [submitExamQuestion, {
    submitExamQuestionData,
    submitExamQuestionLoading,
    submitExamQuestionError,
    submitExamQuestionCalled
  }];
};
export const useValidateExam = (trackId, language) => {
  const [mutate, {
    loading: validateExamLoading,
    data: validateExamData,
    error: validateExamError,
    called: validateExamCalled
  }] = useMutation(VALIDATE_EXAM, {
    refetchQueries: [{
      query: GET_ALL_PROGRESS,
      variables: {
        language
      },
      context: {
        timeout: 25000
      }
    }]
  });
  const validateExam = useCallback(attemptId => {
    const playlistId = getPlaylistIdFromUrl();
    return mutate({
      variables: {
        attemptId,
        trackId,
        playlistId
      },
      update(__, newData) {
        if (newData.data) {
          setMostRecentExamAttemptCache(trackId, newData.data.validateExam);
        }
      }
    }).then(result => result.data && result.data.validateExam);
  }, [mutate, trackId]);
  return [validateExam, {
    validateExamData,
    validateExamLoading,
    validateExamError,
    validateExamCalled
  }];
};
export const useSendEmailBreakdown = () => {
  const [mutate, {
    loading: sendEmailBreakdownLoading,
    data: sendEmailBreakdownData,
    error: sendEmailBreakdownError,
    called: sendEmailBreakdownCalled
  }] = useMutation(SEND_EMAIL_BREAKDOWN);
  const sendEmailBreakdown = useCallback((attemptId, trackId, language = getLang()) => {
    return mutate({
      variables: {
        attemptId,
        trackId,
        language
      }
    }).then(result => result.data);
  }, [mutate]);
  return [sendEmailBreakdown, {
    sendEmailBreakdownData,
    sendEmailBreakdownLoading,
    sendEmailBreakdownError,
    sendEmailBreakdownCalled
  }];
};
export const useTrackAwardExpiry = trackId => {
  const {
    trackProgress,
    trackProgressLoading,
    trackProgressError
  } = useTrackProgress(Number(trackId));
  const award = trackProgress === null || trackProgress === void 0 ? void 0 : trackProgress.award;
  return {
    isExpired: award && award.isExpired,
    expiresOn: award && award.expiresAt,
    examExpiryLoading: trackProgressLoading,
    examExpiryError: trackProgressError
  };
};
export const useExamAttempts = trackId => {
  const {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError
  } = useTrackProgressById(Number(trackId));
  return {
    examAttempts: trackProgressById ? trackProgressById.examAttempts : undefined,
    trackProgressByIdLoading,
    trackProgressByIdError
  };
};
export const useMostRecentExamAttempt = (trackId, language) => {
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(trackId, language);
  const {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError
  } = useTrackProgressById(Number(trackId));
  let mostRecentExamAttempt = track && track.exam && trackProgressById && trackProgressById.examAttempts[trackProgressById.examIdToRecentAttemptId[track.exam.id]];
  const examAttempts = trackProgressById && Object.values(trackProgressById.examIdToRecentAttemptId).map(attemptId => trackProgressById.examAttempts[attemptId]);
  const latestExamAttempt = examAttempts && maxBy(examAttempts, attempt => attempt.attemptFinishTime);
  if (!mostRecentExamAttempt && latestExamAttempt) {
    mostRecentExamAttempt = latestExamAttempt;
  }
  return {
    mostRecentExamAttempt,
    mostRecentExamAttemptLoading: trackLoading || trackProgressByIdLoading,
    mostRecentExamAttemptError: trackError || trackProgressByIdError
  };
};
export const useExamAttemptExpiry = (trackId, language) => {
  const {
    mostRecentExamAttempt,
    mostRecentExamAttemptLoading,
    mostRecentExamAttemptError
  } = useMostRecentExamAttempt(trackId, language);
  const attemptExpiresOn = mostRecentExamAttempt && mostRecentExamAttempt.attemptFinishTime ? mostRecentExamAttempt.attemptFinishTime + TRACK_AWARD_EXPIRY_DAYS * MS_IN_A_DAY : null;
  const isAttemptExpired = attemptExpiresOn ? new Date().getTime() > attemptExpiresOn : false;
  return {
    attemptExpiresOn,
    isAttemptExpired,
    attemptExpiryLoading: mostRecentExamAttemptLoading,
    attemptExpiryError: mostRecentExamAttemptError
  };
};
export const useIsExamAttemptInProgress = (trackId, language) => {
  const {
    mostRecentExamAttempt,
    mostRecentExamAttemptLoading,
    mostRecentExamAttemptError
  } = useMostRecentExamAttempt(trackId, language);
  return {
    isExamAttemptInProgress: mostRecentExamAttempt ? !mostRecentExamAttempt.attemptFinishTime : false,
    isExamAttemptInProgressLoading: mostRecentExamAttemptLoading,
    isExamAttemptInProgressError: mostRecentExamAttemptError
  };
};
export const useExamDeadline = (trackId, language) => {
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(trackId, language);
  const {
    mostRecentExamAttempt,
    mostRecentExamAttemptLoading,
    mostRecentExamAttemptError
  } = useMostRecentExamAttempt(trackId, language);
  const examDeadline = track && track.exam && mostRecentExamAttempt && mostRecentExamAttempt.attemptStartTime + track.exam.durationMinutes * MS_IN_A_MINUTE;
  return {
    examDeadline,
    examDeadlineLoading: trackLoading || mostRecentExamAttemptLoading,
    examDeadlineError: trackError || mostRecentExamAttemptError
  };
};